<template>
  <div 
    class="patient-sms-button"
    :class="{ 
      'has-unread': unreadCount > 0, 
      'pulsing': hasNewMessage,
      'new-sms': !hasExistingConversation,
      'loading': isLoading,
      'feature-disabled': !canSendSms
    }"
    @click="!isLoading && $emit('click')"
    :title="isLoading ? 'Loading...' : buttonTooltip"
  >
    <i v-if="isLoading" class="fa fa-spinner fa-spin"></i>
    <i v-else class="fa" :class="buttonIcon"></i>
    <span v-if="unreadCount > 0 && !isLoading" class="unread-badge">{{ unreadCount }}</span>
    
    <!-- Tooltip -->
    <div class="tooltip-text">
      {{ tooltipText }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'PatientSmsButton',
  props: {
    unreadCount: {
      type: Number,
      default: 0
    },
    hasExistingConversation: {
      type: Boolean,
      default: false
    },
    patientName: {
      type: String,
      default: 'Patient'
    },
    hasNewMessage: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    canSendSms: {
      type: Boolean,
      default: true
    },
    smsDisabledReason: {
      type: String,
      default: null
    }
  },
  computed: {
    buttonIcon() {
      return this.hasExistingConversation ? 'fa-mobile' : 'fa-mobile-phone'
    },
    
    buttonTooltip() {
      if (!this.canSendSms) {
        return this.smsDisabledReason || 'SMS is not available for this patient'
      }
      
      if (!this.hasExistingConversation) {
        return `Start SMS conversation with ${this.patientName}`
      }
      
      if (this.unreadCount > 0) {
        return `${this.unreadCount} unread SMS message${this.unreadCount > 1 ? 's' : ''} from ${this.patientName}`
      }
      
      return `SMS with ${this.patientName}`
    },
    
    tooltipText() {
      if (!this.canSendSms) {
        // Check if it's a feature-level issue vs patient-level issue
        const reason = this.smsDisabledReason || 'SMS is not available for this patient';
        if (reason.includes('not enabled on your account')) {
          return 'Click to learn about SMS messaging for your practice';
        }
        return reason;
      }
      
      if (!this.hasExistingConversation) {
        return `Start SMS conversation with ${this.patientName}`;
      }
      
      return `Continue SMS conversation with ${this.patientName}`;
    }
  }
}
</script>

<style lang="scss" scoped>
.patient-sms-button {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #1976d2;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: all 0.3s ease;
  
  &:hover {
    background: #1565c0;
    transform: scale(1.05);
    
    .tooltip-text {
      opacity: 1;
      visibility: visible;
    }
  }
  
  .fa {
    font-size: 24px;
  }
  
  &.new-sms {
    background: #4caf50;
    
    &:hover {
      background: #43a047;
    }
  }
  
  &.has-unread {
    background: #f44336;
    
    &:hover {
      background: #e53935;
    }
  }
  
  &.pulsing {
    animation: pulse 2s infinite;
  }
  
  &.loading {
    opacity: 0.8;
    cursor: not-allowed;
    
    &:hover {
      transform: none;
    }
  }
  
  &.feature-disabled {
    background: linear-gradient(135deg, #4CAF50, #2196F3);
    cursor: pointer;
    opacity: 0.9;
    
    &:hover {
      background: linear-gradient(135deg, #45a049, #1976D2);
      transform: scale(1.1);
    }
    
    &::after {
      content: '!';
      position: absolute;
      top: -2px;
      right: -2px;
      background: #FF9800;
      color: white;
      border-radius: 50%;
      width: 16px;
      height: 16px;
      font-size: 10px;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  
  .unread-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background: #ff5722;
    color: white;
    border-radius: 12px;
    padding: 2px 6px;
    font-size: 12px;
    font-weight: bold;
    min-width: 20px;
    text-align: center;
    border: 2px solid white;
  }
  
  .tooltip-text {
    position: absolute;
    bottom: 70px;
    right: 0;
    background: rgba(0,0,0,0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
    z-index: 1001;
    
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      right: 20px;
      border: 5px solid transparent;
      border-top-color: rgba(0,0,0,0.8);
    }
  }
}

@keyframes pulse {
  0% { 
    box-shadow: 0 4px 12px rgba(0,0,0,0.15), 0 0 0 0 rgba(25, 118, 210, 0.7); 
  }
  70% { 
    box-shadow: 0 4px 12px rgba(0,0,0,0.15), 0 0 0 10px rgba(25, 118, 210, 0); 
  }
  100% { 
    box-shadow: 0 4px 12px rgba(0,0,0,0.15), 0 0 0 0 rgba(25, 118, 210, 0); 
  }
}

// Mobile responsiveness
@media (max-width: 768px) {
  .patient-sms-button {
    width: 50px;
    height: 50px;
    
    .fa {
      font-size: 20px;
    }
    
    .tooltip-text {
      bottom: 60px;
      right: -10px;
    }
  }
}
</style>
